import { ExternalLink, Instagram, UserCheck, Users } from 'lucide-react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { instagramFetchProfile } from 'sprites-types/workflow/tools/instagramFetchProfile'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function InstagramFetchProfileDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(instagramFetchProfile, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const profile = result
  const username = (profile as any)?.username || (profile as any)?.handle || 'Unknown'
  const fullName =
    (profile as any)?.full_name || (profile as any)?.name || (profile as any)?.displayName
  const bio = (profile as any)?.biography || (profile as any)?.bio || (profile as any)?.description
  const followers =
    (profile as any)?.followers_count ||
    (profile as any)?.followers ||
    (profile as any)?.follower_count
  const following =
    (profile as any)?.following_count ||
    (profile as any)?.following ||
    (profile as any)?.follows_count
  const posts =
    (profile as any)?.media_count || (profile as any)?.posts || (profile as any)?.post_count
  const verified = (profile as any)?.is_verified || (profile as any)?.verified
  const profilePicUrl =
    (profile as any)?.profile_pic_url || (profile as any)?.avatar || (profile as any)?.profile_image
  const profileUrl =
    (profile as any)?.external_url || (profile as any)?.url || `https://instagram.com/${username}`

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-start gap-4 px-4 py-4">
        {/* Profile Picture */}
        <div className="flex-shrink-0">
          {profilePicUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={profilePicUrl}
              alt={`${username} profile`}
              className="h-16 w-16 rounded-full border-2 border-pink-200 object-cover"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-pink-500 to-orange-400">
              <Instagram className="h-8 w-8 text-white" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          {/* Header */}
          <div className="mb-2 flex items-center gap-2">
            <div className="text-sm font-bold text-gray-900">@{username}</div>
            {verified && <UserCheck className="h-4 w-4 text-blue-500" />}
          </div>

          {fullName && <div className="mb-2 text-sm text-gray-700">{fullName}</div>}

          {/* Stats */}
          <div className="mb-3 flex items-center gap-4 text-xs text-gray-600">
            {posts !== undefined && (
              <div className="flex items-center gap-1">
                <div className="font-semibold">{posts.toLocaleString()}</div>
                <div>posts</div>
              </div>
            )}
            {followers !== undefined && (
              <div className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                <div className="font-semibold">{followers.toLocaleString()}</div>
                <div>followers</div>
              </div>
            )}
            {following !== undefined && (
              <div className="flex items-center gap-1">
                <div className="font-semibold">{following.toLocaleString()}</div>
                <div>following</div>
              </div>
            )}
          </div>

          {/* Bio */}
          {bio && <div className="mb-3 line-clamp-3 text-xs text-gray-700">{bio}</div>}

          {/* Link */}
          {profileUrl && (
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-pink-600 hover:text-pink-800"
            >
              <ExternalLink className="h-3 w-3" />
              View on Instagram
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function downloadInstagramFetchProfile(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function instagramFetchProfileToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(instagramFetchProfile, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data

  const profile = result
  const username = (profile as any)?.username || (profile as any)?.handle || 'Unknown'
  const fullName =
    (profile as any)?.full_name || (profile as any)?.name || (profile as any)?.displayName
  const bio = (profile as any)?.biography || (profile as any)?.bio || (profile as any)?.description
  const followers =
    (profile as any)?.followers_count ||
    (profile as any)?.followers ||
    (profile as any)?.follower_count
  const following =
    (profile as any)?.following_count ||
    (profile as any)?.following ||
    (profile as any)?.follows_count
  const posts =
    (profile as any)?.media_count || (profile as any)?.posts || (profile as any)?.post_count
  const verified = (profile as any)?.is_verified || (profile as any)?.verified

  return `## Instagram Profile: @${username}

${fullName ? `**Name:** ${fullName}` : ''}
${verified ? '**Verified:** ✓' : ''}
${posts !== undefined ? `**Posts:** ${posts.toLocaleString()}` : ''}
${followers !== undefined ? `**Followers:** ${followers.toLocaleString()}` : ''}
${following !== undefined ? `**Following:** ${following.toLocaleString()}` : ''}

${bio ? `**Bio:** ${bio}` : ''}

---
`
}

export const InstagramFetchProfileDisplay = createWorkflowToolComponent(
  InstagramFetchProfileDisplayComponent,
  downloadInstagramFetchProfile,
  instagramFetchProfileToMarkdown
)
